'use client';

import { ButtonPlay } from '@/components/_rebrand/ButtonPlay';
import Grid from '@/components/_rebrand/Grid';
import ThemeWrapper from '@/components/_rebrand/ThemeWrapper';
import gtm from '@/lib/gtm';
import { ThemeNameType } from '@/tailwind-config/theme.constants';
import { WistiaPlayer } from '@wistia/wistia-player-react';
import cn from 'classnames';
import Image from 'next/image';
import { FC } from 'react';
import { TextCombination } from '../TextCombination';
import { useFullBleedVideo } from './FullBleedVideo.hooks';
import { FullBleedVideoFeatureItem } from './FullBleedVideo.types';

export type FullBleedVideoProps = {
  eyebrow?: string;
  title: string;
  description?: string;
  highlightCards: FullBleedVideoFeatureItem[];
  mediaId: string;
  posterSrc?: string;
  posterAlt?: string;
  previewVideoMediaId?: string;
  theme?: ThemeNameType;
};

/*
 * Wistia video section
 */
const FullBleedVideo: FC<FullBleedVideoProps> = ({
  eyebrow,
  title,
  description,
  highlightCards,
  mediaId,
  posterSrc,
  posterAlt,
  previewVideoMediaId,
  theme = 'theme-white',
}) => {
  const {
    isPlaying,
    shouldMountPlayer,
    isPreviewReady,
    setIsPreviewReady,
    hasTrackedPlay,
    handlePlayerApiReady,
    preloadPlayer,
    handlePlay,
  } = useFullBleedVideo();

  return (
    <ThemeWrapper theme={theme}>
      <div className="surface-l0 py-margin-section-md">
        <Grid className="gap-y-3xl">
          <TextCombination
            type="h2p2"
            align="center"
            headingLevel="h2"
            eyebrow={eyebrow}
            title={title}
            description={description}
            className="col-span-4 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 whitespace-pre-line"
          />

          <div className="col-span-4 md:col-span-10 md:col-start-2">
            <div className="relative aspect-video w-full overflow-hidden rounded-s">
              {shouldMountPlayer && (
                <WistiaPlayer
                  mediaId={mediaId}
                  preload="metadata"
                  autoplay={false}
                  bigPlayButton={false}
                  swatch={false}
                  fitStrategy="cover"
                  style={{ width: '100%', height: '100%' }}
                  onApiReady={handlePlayerApiReady}
                  onPlay={() => {
                    if (hasTrackedPlay.current) return;
                    hasTrackedPlay.current = true;

                    gtm.track('play_video', {
                      video_title: title.replaceAll('\n', ' '),
                      video_wistia_id: mediaId,
                    });
                  }}
                />
              )}
              <div
                className={cn('absolute inset-0 transition-opacity duration-1000 ease-out', {
                  'pointer-events-none opacity-0': isPlaying,
                  'cursor-pointer opacity-100': !isPlaying,
                })}
                onClick={isPlaying ? undefined : handlePlay}
                onMouseEnter={isPlaying ? undefined : preloadPlayer}
                onTouchStart={isPlaying ? undefined : preloadPlayer}
              >
                {previewVideoMediaId ? (
                  <WistiaPlayer
                    mediaId={previewVideoMediaId}
                    autoplay
                    muted
                    endVideoBehavior="loop"
                    bigPlayButton={false}
                    playPauseControl={false}
                    playBarControl={false}
                    volumeControl={false}
                    settingsControl={false}
                    qualityControl={false}
                    fullscreenControl={false}
                    controlsVisibleOnLoad={false}
                    swatch={false}
                    fitStrategy="cover"
                    className={cn('absolute inset-0 transition-opacity duration-1000 ease-out', {
                      'opacity-0': !isPreviewReady,
                      'opacity-100': isPreviewReady,
                    })}
                    style={{ width: '100%', height: '100%' }}
                    onApiReady={() => setIsPreviewReady(true)}
                  />
                ) : (
                  <Image src={posterSrc ?? ''} alt={posterAlt ?? ''} fill className="object-cover" sizes="100vw" />
                )}
                {(!previewVideoMediaId || isPreviewReady) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="absolute inset-0 w-full h-full bg-gray-900 opacity-15 z-0" />
                    <div className="relative z-10">
                      <ButtonPlay text="Play" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {highlightCards.map((item) => (
            <TextCombination
              key={item.title}
              type="p1"
              align="center"
              headingLevel="h3"
              title={item.title}
              description={item.description}
              className="col-span-4"
              isHeadingBalanced={true}
            />
          ))}
        </Grid>
      </div>
    </ThemeWrapper>
  );
};

export default FullBleedVideo;
